import type { FC } from "react";
import { useCallback } from "react";

import { Link } from "@mui/material";
import clsx from "clsx";
import { Link as RouterLink } from "react-router-dom";

import { useUser } from "shared/hooks";
import type { BackendEntityType, EntitySoccerRoles, LogoModel, SearchResultModel } from "shared/types";
import { Button, ImageSizesEnum, Img, SkeletonBase, Text } from "shared/uikit";
import { ButtonSizesEnum, TextVariantsEnum } from "shared/uikit/types";

interface SkeletonProps {
	loading: boolean;
	data?: never;
	followProps?: never;
	className?: never;
	entityUrl?: never;
	handleClose?: never;
}

export interface DataProps {
	loading?: never;
	logo?: LogoModel | null;
	data: SearchResultModel;
	entityUrl?: string;
	className?: string;
	handleClose?: () => void;
	followProps?: {
		id: string;
		type: BackendEntityType | EntitySoccerRoles;
	};
}

type Props = SkeletonProps | DataProps;

const SearchResultBox: FC<Props> = ({ loading, data, className, entityUrl, followProps, handleClose }) => {
	const { followEntity, getData: getUserData } = useUser();
	const { following } = getUserData();

	const handleFollow = useCallback(() => followEntity(followProps!.id, followProps!.type), [followEntity, followProps]);

	if (loading) {
		return (
			<div className="w-full flex items-center gap-2">
				<SkeletonBase width={28} height={28} variant="circular" fullScale />
				<div className="flex flex-col gap-1">
					<SkeletonBase width={90} height={10} fullScale />
					<div className="flex items-center gap-1">
						<SkeletonBase width={70} height={8} fullScale />
						<SkeletonBase width={35} height={8} fullScale />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="flex items-center justify-between gap-2 w-full">
			<Link
				to={`${entityUrl}/${data?.id}`}
				component={RouterLink}
				className={clsx("flex items-center gap-2 w-full no-underline truncate", className)}
				onClick={() => {
					handleClose && handleClose();
				}}
			>
				<Img src={data?.logo} alt={data?.name} circle size={ImageSizesEnum.Small24} className="shrink-0" />
				<div className="flex flex-col">
					<Text variants={TextVariantsEnum.Caption3}>{data?.name}</Text>
					<div className="flex items-center gap-1">
						<Text variants={TextVariantsEnum.Caption}>{data?.address}</Text>
						{/* <Text variants={TextVariantsEnum.Caption} className="text-hint">
							0 miles away
						</Text> */}
					</div>
				</div>
			</Link>
			{followProps && (
				<Button
					secondary
					size={ButtonSizesEnum.XS}
					onClick={handleFollow}
					loading={following === followProps.id}
					disabled={following === followProps.id}
					className="shrink-0"
				>
					Follow
				</Button>
			)}
		</div>
	);
};

export default SearchResultBox;
